/**
 * Diagnostic state collector — Phase 6 sub-commit 8 (13 Jun 2026).
 *
 * Snapshots the browser-side state that a tech-support workflow
 * needs when a guest reports a problem. The collector is purely
 * read-only: it doesn't clear caches, force a SW update, sign
 * the user out, or mutate any state. Observation surface only —
 * action surfaces (with their attendant confirm-undo-safety UX)
 * are a deliberate non-goal of this batch.
 *
 * The output is a structured `DiagnosticReport` JSON object that
 * the DiagnosticsPage renders in a <pre> block alongside a
 * "Copy to clipboard" button. Guest pastes the JSON into a
 * support email; Mick / support reads it to pinpoint:
 *
 *   - Stale service worker (swVersion vs current APP_VERSION drift)
 *   - Cache corruption / oversized buckets (caches[].entryCount,
 *     storage.estimateUsedBytes)
 *   - Stuck outbox messages (outbox.queued / failedRetryable /
 *     oldestCreatedAt)
 *   - Install state confusion (pwa.standalone vs
 *     pwa.installDismissed vs pwa.installAffordance)
 *   - Browser-level offline detection drift (network.online vs
 *     observed app behaviour)
 *   - Locale / timezone surprises (browser.timezone vs the
 *     timezone the server expects)
 *
 * Defensive throughout: every browser API the collector touches
 * can be missing (older mobile WebViews, jsdom in tests, iOS
 * Lockdown Mode). Each section returns null / empty for its
 * own missing-API path, and a top-level try/catch on the orchestrator
 * collapses any unexpected throw into a partially-filled report
 * rather than failing the whole collection. A partial report is
 * far more useful than no report.
 *
 * Service-worker version round-trip:
 *
 *   The SW's `APP_VERSION` constant lives inside the worker
 *   process and isn't directly readable from the page. We
 *   message-channel postMessage `{ type: 'GET_VERSION' }` and
 *   await a `{ type: 'VERSION', version }` reply on the channel's
 *   port1. Handler added to public/sw.js in the same commit.
 *   1.5s timeout — a non-responsive SW shouldn't hang the
 *   collector indefinitely. Timeout collapses to swVersion: null
 *   which itself is a diagnostic signal ("SW present but not
 *   responding to messages").
 */

import { readOutbox, type OutboxItem } from './outbox';
import {
  getDeferredPrompt,
  isIos,
  readInstallNudge,
} from './installNudge';

// ─── Report shape ────────────────────────────────────────────────────

export interface DiagnosticReport {
  /** ISO timestamp of when this snapshot was taken. */
  generatedAt: string;
  app: {
    /**
     * The app's build hash / version string injected by Vite at
     * build time via `import.meta.env.VITE_APP_VERSION`. Returns
     * null when the env var isn't set (local dev, or a build
     * pipeline that hasn't been configured to expose it).
     */
    buildVersion: string | null;
    locale: string;
    pathname: string;
  };
  user: {
    /** Clerk user id, or null when signed out. */
    userId: string | null;
  };
  pwa: {
    /** True when running in `display-mode: standalone` — i.e.
     *  launched from the home-screen icon, not the browser tab. */
    standalone: boolean;
    /**
     * Best-effort detection of what install path is available:
     *
     *   - `native-prompt-available`: cached `beforeinstallprompt`
     *     event in hand, ready to invoke the native dialog
     *     (Android Chrome, desktop Chromium).
     *   - `ios`: detected iOS UA (iPhone, iPad, iPod, iPadOS in
     *     Desktop Mode). The install path is Share → Add to
     *     Home Screen; no programmatic API.
     *   - `unknown`: neither — could be Firefox desktop, an older
     *     browser without PWA install support, or simply that
     *     the page loaded before the browser fired its
     *     beforeinstallprompt event.
     */
    installAffordance: 'native-prompt-available' | 'ios' | 'unknown';
    /** True when the guest has previously dismissed the install
     *  nudge (regardless of whether they declined or installed). */
    installDismissed: boolean;
    /** Reason the install nudge was dismissed, when known. */
    installDismissedReason: 'declined' | 'installed' | null;
  };
  serviceWorker: {
    state: 'active' | 'installing' | 'none';
    scriptUrl: string | null;
    /** APP_VERSION constant from the running SW, or null when
     *  the SW is absent / unresponsive. */
    swVersion: string | null;
  };
  /** Every cache the Cache API exposes, with entry counts. Entry
   *  counts are the structural diagnostic — large counts indicate
   *  the SW has been caching aggressively (which is the goal of
   *  Phase 6) and small / zero counts indicate cache-purge events
   *  or a fresh install. */
  caches: Array<{ name: string; entryCount: number }>;
  outbox: {
    queued: number;
    sending: number;
    failedRetryable: number;
    failedPermanent: number;
    /** ISO timestamp of the oldest item still in the outbox,
     *  regardless of status. Indicates how long the user has had
     *  a stuck message. */
    oldestCreatedAt: string | null;
  };
  network: {
    online: boolean;
    /** From the non-standard Network Information API
     *  (navigator.connection). Returns null on browsers that
     *  don't expose it (Safari, Firefox). Values include '4g',
     *  '3g', '2g', 'slow-2g'. */
    connectionType: string | null;
  };
  storage: {
    /** Bytes used by this origin, per navigator.storage.estimate().
     *  Null on browsers without the Storage Estimate API. */
    estimateUsedBytes: number | null;
    estimateQuotaBytes: number | null;
    /** Convenience field — used/quota * 100, rounded. Helps
     *  quickly spot near-quota conditions where future writes
     *  might fail. */
    estimatePercentUsed: number | null;
  };
  browser: {
    userAgent: string;
    language: string;
    /** IANA timezone string (e.g. 'Europe/London'). Diagnostic
     *  for date-local mismatches between client and server. */
    timezone: string;
  };
}

// ─── Top-level orchestrator ──────────────────────────────────────────

export interface CollectDiagnosticsOpts {
  userId: string | null;
}

/**
 * Collect a full diagnostic snapshot. Async because several
 * sources (service worker, caches, storage estimate) require
 * await. Total runtime is typically <100ms; the SW round-trip
 * is the slowest path and is capped at 1.5s.
 */
export async function collectDiagnostics(
  opts: CollectDiagnosticsOpts,
): Promise<DiagnosticReport> {
  const [pwa, serviceWorker, cacheList, storage] = await Promise.all([
    collectPwa(opts.userId),
    collectServiceWorker(),
    collectCaches(),
    collectStorage(),
  ]);
  return {
    generatedAt: new Date().toISOString(),
    app: collectApp(),
    user: { userId: opts.userId },
    pwa,
    serviceWorker,
    caches: cacheList,
    outbox: collectOutbox(opts.userId),
    network: collectNetwork(),
    storage,
    browser: collectBrowser(),
  };
}

// ─── Section collectors ──────────────────────────────────────────────

function collectApp(): DiagnosticReport['app'] {
  // Vite exposes import.meta.env at build time. We try a couple
  // of conventional names so this works regardless of which the
  // build pipeline ends up wiring (VITE_APP_VERSION is the
  // documented Vite convention; VITE_BUILD_HASH is a common
  // alternative for commit-hash injection). Returns null when
  // neither is set — better than a synthetic placeholder that
  // looks like a real version string.
  const env = import.meta.env as Record<string, string | undefined>;
  const buildVersion =
    env.VITE_APP_VERSION ?? env.VITE_BUILD_HASH ?? null;
  return {
    buildVersion,
    locale: typeof navigator !== 'undefined' ? navigator.language : 'unknown',
    pathname:
      typeof window !== 'undefined' ? window.location.pathname : 'unknown',
  };
}

async function collectPwa(
  userId: string | null,
): Promise<DiagnosticReport['pwa']> {
  const standalone =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(display-mode: standalone)').matches;
  let installAffordance: DiagnosticReport['pwa']['installAffordance'] =
    'unknown';
  if (getDeferredPrompt()) {
    installAffordance = 'native-prompt-available';
  } else if (isIos()) {
    installAffordance = 'ios';
  }
  const nudgeState = readInstallNudge(userId);
  return {
    standalone,
    installAffordance,
    installDismissed: nudgeState !== null,
    installDismissedReason: nudgeState?.dismissedReason ?? null,
  };
}

async function collectServiceWorker(): Promise<
  DiagnosticReport['serviceWorker']
> {
  if (
    typeof navigator === 'undefined' ||
    !('serviceWorker' in navigator)
  ) {
    return { state: 'none', scriptUrl: null, swVersion: null };
  }
  try {
    const registration =
      await navigator.serviceWorker.getRegistration();
    if (!registration) {
      return { state: 'none', scriptUrl: null, swVersion: null };
    }
    const active = registration.active;
    if (active) {
      const swVersion = await requestSwVersion();
      return {
        state: 'active',
        scriptUrl: active.scriptURL,
        swVersion,
      };
    }
    const installing = registration.installing;
    if (installing) {
      return {
        state: 'installing',
        scriptUrl: installing.scriptURL,
        swVersion: null,
      };
    }
    return { state: 'none', scriptUrl: null, swVersion: null };
  } catch {
    return { state: 'none', scriptUrl: null, swVersion: null };
  }
}

/**
 * Round-trip GET_VERSION to the active service worker. Uses a
 * MessageChannel so the response comes back on a private port
 * rather than broadcasting via navigator.serviceWorker.onmessage
 * (which would race with the app's own SW message listeners,
 * notably the announcement push-relay handler in MePage).
 *
 * 1500ms timeout — generous enough that a busy SW finishes any
 * in-flight work first, tight enough that the diagnostics page
 * doesn't appear to hang. Timeout collapses to null which is
 * itself diagnostic ("SW present but unresponsive").
 */
function requestSwVersion(): Promise<string | null> {
  return new Promise((resolve) => {
    if (
      typeof navigator === 'undefined' ||
      !navigator.serviceWorker?.controller
    ) {
      resolve(null);
      return;
    }
    const channel = new MessageChannel();
    const timeout = setTimeout(() => {
      resolve(null);
    }, 1500);
    channel.port1.onmessage = (event) => {
      clearTimeout(timeout);
      const data = event.data;
      if (
        data &&
        typeof data === 'object' &&
        data.type === 'VERSION' &&
        typeof data.version === 'string'
      ) {
        resolve(data.version);
      } else {
        resolve(null);
      }
    };
    try {
      navigator.serviceWorker.controller.postMessage(
        { type: 'GET_VERSION' },
        [channel.port2],
      );
    } catch {
      clearTimeout(timeout);
      resolve(null);
    }
  });
}

async function collectCaches(): Promise<DiagnosticReport['caches']> {
  if (typeof caches === 'undefined') return [];
  try {
    const names = await caches.keys();
    const results = await Promise.all(
      names.map(async (name) => {
        try {
          const cache = await caches.open(name);
          const keys = await cache.keys();
          return { name, entryCount: keys.length };
        } catch {
          return { name, entryCount: -1 };
        }
      }),
    );
    return results;
  } catch {
    return [];
  }
}

export function collectOutbox(
  userId: string | null,
): DiagnosticReport['outbox'] {
  if (!userId) {
    return {
      queued: 0,
      sending: 0,
      failedRetryable: 0,
      failedPermanent: 0,
      oldestCreatedAt: null,
    };
  }
  const items: OutboxItem[] = readOutbox(userId);
  let queued = 0;
  let sending = 0;
  let failedRetryable = 0;
  let failedPermanent = 0;
  let oldestCreatedAt: string | null = null;
  for (const item of items) {
    if (item.status === 'queued') queued++;
    else if (item.status === 'sending') sending++;
    else if (item.status === 'failed-retryable') failedRetryable++;
    else if (item.status === 'failed-permanent') failedPermanent++;
    if (
      oldestCreatedAt === null ||
      item.createdAt < oldestCreatedAt
    ) {
      oldestCreatedAt = item.createdAt;
    }
  }
  return {
    queued,
    sending,
    failedRetryable,
    failedPermanent,
    oldestCreatedAt,
  };
}

function collectNetwork(): DiagnosticReport['network'] {
  const online =
    typeof navigator !== 'undefined' ? navigator.onLine : true;
  // The Network Information API is non-standard and not in the
  // TypeScript DOM lib. Cast to a narrow shape for the read.
  let connectionType: string | null = null;
  if (typeof navigator !== 'undefined') {
    const conn = (
      navigator as Navigator & {
        connection?: { effectiveType?: string };
      }
    ).connection;
    if (conn && typeof conn.effectiveType === 'string') {
      connectionType = conn.effectiveType;
    }
  }
  return { online, connectionType };
}

async function collectStorage(): Promise<DiagnosticReport['storage']> {
  const empty = {
    estimateUsedBytes: null,
    estimateQuotaBytes: null,
    estimatePercentUsed: null,
  };
  if (
    typeof navigator === 'undefined' ||
    !navigator.storage ||
    typeof navigator.storage.estimate !== 'function'
  ) {
    return empty;
  }
  try {
    const estimate = await navigator.storage.estimate();
    const used =
      typeof estimate.usage === 'number' ? estimate.usage : null;
    const quota =
      typeof estimate.quota === 'number' ? estimate.quota : null;
    const percent =
      used !== null && quota !== null && quota > 0
        ? Math.round((used / quota) * 100)
        : null;
    return {
      estimateUsedBytes: used,
      estimateQuotaBytes: quota,
      estimatePercentUsed: percent,
    };
  } catch {
    return empty;
  }
}

function collectBrowser(): DiagnosticReport['browser'] {
  return {
    userAgent:
      typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
    language:
      typeof navigator !== 'undefined' ? navigator.language : 'unknown',
    timezone:
      typeof Intl !== 'undefined'
        ? Intl.DateTimeFormat().resolvedOptions().timeZone
        : 'unknown',
  };
}
